if (!customElements.get('bundle-contents-marine')) {
  customElements.define(
    'bundle-contents-marine',
    class BundleContentsMarine extends HTMLElement {
      static API_URL = '/api/2026-07/graphql.json';

      static QUERY = `query BundleContents($handle: String!) {
        product(handle: $handle) {
          variants(first: 100) {
            nodes {
              id
              requiresComponents
              components(first: 30) {
                nodes {
                  quantity
                  productVariant {
                    id
                    title
                    image { url altText width height }
                    product { title handle featuredImage { url altText width height } }
                  }
                }
              }
            }
          }
        }
      }`;

      constructor() {
        super();
        this.variants = null;
        this.anchor = null;
        this.nav = null;
        this.initialized = false;
        this.onVariantChange = this.onVariantChange.bind(this);
        this.place = this.place.bind(this);
        this.updateNav = this.updateNav.bind(this);
      }

      connectedCallback() {
        // Moving the block under the product photo reconnects it; it must not load twice.
        if (this.initialized || !this.dataset.productHandle) return;
        this.initialized = true;
        this.phoneQuery = window.matchMedia('(max-width: 749px)');
        this.phoneQuery.addEventListener('change', this.place);
        document.addEventListener('afterVariantChanged', this.onVariantChange);
        window.addEventListener('resize', this.updateNav);
        this.load();
      }

      disconnectedCallback() {
        window.setTimeout(() => {
          if (this.isConnected) return;
          document.removeEventListener('afterVariantChanged', this.onVariantChange);
          this.phoneQuery?.removeEventListener('change', this.place);
          window.removeEventListener('resize', this.updateNav);
          this.initialized = false;
        });
      }

      place() {
        this.moveForScreen();
        this.updateNav();
      }

      // On phones the list sits right under the product photo; on wider screens it stays in its own section.
      moveForScreen() {
        if (this.hidden || window.Shopify?.designMode) return;
        const onPhone = this.phoneQuery?.matches;
        if (onPhone && !this.anchor) {
          const media = document.querySelector('product-info .marine-product-info__media-column');
          if (!media) return;
          this.anchor = document.createComment('bundle-contents-marine');
          this.before(this.anchor);
          media.after(this);
          this.classList.add('bundle-contents-marine--inline');
        } else if (!onPhone && this.anchor) {
          this.anchor.after(this);
          this.anchor.remove();
          this.anchor = null;
          this.classList.remove('bundle-contents-marine--inline');
        }
      }

      async load() {
        try {
          const response = await fetch(BundleContentsMarine.API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              query: BundleContentsMarine.QUERY,
              variables: { handle: this.dataset.productHandle },
            }),
          });
          if (!response.ok) return;
          const json = await response.json();
          const nodes = json?.data?.product?.variants?.nodes;
          if (!Array.isArray(nodes)) return;
          this.variants = new Map(nodes.map((node) => [this.numericId(node.id), node]));
          this.render(this.dataset.variantId);
        } catch (error) {
          this.hidden = true;
        }
      }

      onVariantChange(event) {
        const variantId = event?.detail?.variant?.id;
        // The event also fires for quick view of other products; only this product's variants count.
        if (!variantId || !this.variants?.has(String(variantId))) return;
        this.render(variantId);
      }

      render(variantId) {
        const variant = this.variants?.get(String(variantId));
        const components = variant?.requiresComponents ? variant.components?.nodes || [] : [];
        const list = this.querySelector('[data-bundle-list]');
        const template = this.querySelector('template[data-bundle-card]');

        // A component whose product is not published comes back empty. Showing the rest
        // would list fewer items than the bundle holds, so the section stays hidden.
        const complete = components.length > 0 && components.every((component) => component?.productVariant?.product?.handle);
        if (!complete || !list || !template) {
          this.hidden = true;
          return;
        }

        list.replaceChildren(...components.map((component) => this.buildCard(template, component)));
        list.scrollLeft = 0;

        const summary = this.querySelector('[data-bundle-summary]');
        if (summary) {
          const pieces = components.reduce((total, component) => total + (component.quantity || 0), 0);
          summary.textContent = (this.dataset.summary || '')
            .replace('[items]', String(components.length))
            .replace('[pieces]', String(pieces));
        }

        this.hidden = false;
        this.setupNav();
        this.place();
      }

      // From 750px the list is one row; the arrows page through it and show only when it overflows.
      setupNav() {
        if (this.nav) return;
        const nav = this.querySelector('[data-bundle-nav]');
        const list = this.querySelector('[data-bundle-list]');
        const prev = nav?.querySelector('[data-bundle-prev]');
        const next = nav?.querySelector('[data-bundle-next]');
        if (!nav || !list || !prev || !next) return;
        this.nav = { nav, list, prev, next };
        prev.addEventListener('click', () => this.scrollPage(-1));
        next.addEventListener('click', () => this.scrollPage(1));
        list.addEventListener('scroll', this.updateNav, { passive: true });
      }

      updateNav() {
        if (!this.nav) return;
        const { nav, list, prev, next } = this.nav;
        const max = list.scrollWidth - list.clientWidth;
        // The phone row under the photo is swiped, it has no arrows.
        nav.hidden = this.hidden || this.classList.contains('bundle-contents-marine--inline') || max <= 1;
        if (nav.hidden) return;
        const position = Math.abs(list.scrollLeft);
        prev.disabled = position <= 1;
        next.disabled = position >= max - 1;
      }

      scrollPage(step) {
        if (!this.nav) return;
        const { list } = this.nav;
        const direction = window.getComputedStyle(list).direction === 'rtl' ? -1 : 1;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        list.scrollBy({ left: step * direction * list.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      buildCard(template, component) {
        const fragment = template.content.cloneNode(true);
        const variant = component.productVariant;
        const product = variant.product;

        const link = fragment.querySelector('[data-card-link]');
        if (link) {
          const root = window.Shopify?.routes?.root || '/';
          link.href = `${root}products/${encodeURIComponent(product.handle)}?variant=${this.numericId(variant.id)}`;
        }

        const title = fragment.querySelector('[data-card-title]');
        if (title) title.textContent = product.title;

        const variantLine = fragment.querySelector('[data-card-variant]');
        if (variantLine) {
          if (variant.title && variant.title !== 'Default Title') {
            variantLine.textContent = variant.title;
          } else {
            variantLine.remove();
          }
        }

        const quantity = fragment.querySelector('[data-card-qty]');
        if (quantity) quantity.textContent = `${this.dataset.quantityPrefix || ''}${component.quantity}`;

        const image = variant.image || product.featuredImage;
        const img = fragment.querySelector('[data-card-image]');
        const placeholder = fragment.querySelector('.bundle-contents-marine__placeholder');
        if (img && image?.url) {
          img.src = this.sizedUrl(image.url, 360);
          img.srcset = `${this.sizedUrl(image.url, 360)} 360w, ${this.sizedUrl(image.url, 720)} 720w`;
          img.sizes = '(min-width: 990px) 20vw, (min-width: 750px) 33vw, 50vw';
          img.alt = image.altText || product.title;
          if (image.width && image.height) {
            img.width = image.width;
            img.height = image.height;
          }
          placeholder?.remove();
        } else {
          img?.remove();
        }

        return fragment;
      }

      sizedUrl(url, width) {
        return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
      }

      numericId(gid) {
        return String(gid || '').split('/').pop();
      }
    }
  );
}
